import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { CircleCheck, MapPin } from 'lucide-react';
import { BRAND, EXAM_TYPE_LABEL } from '@dru/types';
import { Link } from 'react-router-dom';
import type {
  CampaignVenue, CatalogueTest, CursorPage, EmployeeRow, ExaminationOrderRow,
  ExaminationReadiness, Partner, PartnerAvailability, PartnerLocation, PhysicianRow,
} from '@dru/types';
import {
  Alert, Button, Dialog, EntityCombobox, InfoTip, Input, SearchSelect, Select, Steps, TextField,
  cn, useIdempotencyKey,
} from '@dru/ui';
import { api } from '../../../lib/api';
import { otherBranches } from '../../../lib/branches';
import { useQuery } from '../../../lib/useQuery';
import { errorMessage, useAuth } from '../../../lib/auth';
import type { CompanyPackage } from '../lib/companyPackages';

/** One vocabulary for the examination type, wherever it is chosen. */
export const EXAM_TYPES: Array<[string, string]> = [
  ['PERIODIC_OHC', 'Periodic health checkup'],
  ['ANNUAL_HEALTH_CHECKUP', 'Annual health checkup'],
  ['PRE_EMPLOYMENT', 'Pre-employment'],
  ['RETURN_TO_WORK', 'Return to work'],
  ['POST_INCIDENT', 'Post-incident'],
  ['SPECIAL', 'Special'],
  ['EXIT', 'Exit'],
];

const pad2 = (n: number) => String(n).padStart(2, '0');
const dayOf = (d: Date) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

/** YYYY-MM-DD in the browser's timezone. */
export function localDay(): string {
  return dayOf(new Date());
}

const TIMES = Array.from({ length: (19 - 7) * 4 }, (_, i) => `${String(7 + Math.floor(i / 4)).padStart(2, '0')}:${['00', '15', '30', '45'][i % 4]}`);

/** How far ahead of now the first offered slot is: nobody books a visit for ten minutes' time. */
const LEAD_MINUTES = 30;

/**
 * The times still bookable on a day. Today loses the ones already gone (plus
 * the lead); the dialog offered 07:00 at 11:37, and the server then refused
 * the booking after the case had been raised (flow audit 2026-10-20, 5.2).
 */
export function timesOn(day: string, now = new Date()): string[] {
  if (day !== dayOf(now)) return TIMES;
  const earliest = new Date(now.getTime() + LEAD_MINUTES * 60_000);
  const hhmm = `${pad2(earliest.getHours())}:${pad2(earliest.getMinutes())}`;
  // A lead that runs past midnight leaves nothing today.
  if (dayOf(earliest) !== day) return [];
  return TIMES.filter((t) => t >= hhmm);
}

/** The next day the venues work: Sunday is skipped. */
function nextWorkingDay(day: string): string {
  const [y, m, d] = day.split('-').map(Number);
  const next = new Date(y!, m! - 1, d! + 1);
  if (next.getDay() === 0) next.setDate(next.getDate() + 1);
  return dayOf(next);
}

/**
 * Where the date and time pickers start: the next slot still ahead — today
 * when the day has room left, otherwise 09:30 on the next working day.
 */
export function firstFreeSlot(now = new Date()): { date: string; time: string } {
  const today = dayOf(now);
  const left = timesOn(today, now);
  if (left.length && now.getDay() !== 0) {
    return { date: today, time: left.includes('09:30') ? '09:30' : left[0]! };
  }
  return { date: nextWorkingDay(today), time: '09:30' };
}

/** Why this slot cannot be booked, before anything is sent. */
function slotProblem(date: string, time: string): string | null {
  if (!date || !time) return 'Choose a date and a time.';
  if (new Date(`${date}T${time}:00`).getTime() <= Date.now()) {
    return 'That time has already passed. Choose a later time or another day.';
  }
  return null;
}

/** Cases the dialog can carry on with rather than refuse: nobody has a slot yet. */
const BOOKABLE = new Set(['REQUESTED', 'ROUTED', 'CONSENT_PENDING']);

/**
 * Arranging an examination: raise, route and book, in one dialog.
 *
 * These were three dialogs on the examinations list — raise, then find the
 * row and route, then find the row again and book — and two of them asked
 * which lab. Here the employee is chosen (or already known, when opened from
 * their page), the lab and physician once, the slot, and one click does all
 * three — in ONE request, `POST /examinations/arrange`, so a slot the server
 * refuses leaves no raised-but-unbooked case behind (flow audit 2026-10-20,
 * 5.2; the three calls used to be made one after another from here).
 *
 * `order` opens it against an examination that already exists: REQUESTED
 * skips the raise, ROUTED / CONSENT_PENDING skips route too and only books.
 * Opened for an employee whose case is already open and not yet booked, it
 * carries on with that case instead of refusing (5.3).
 */
export function ArrangeDialog({ employee: given, order, onClose, onDone, continued }: {
  employee?: { id: string; fullName: string; employeeCode: string; status?: string } | null;
  order?: ExaminationOrderRow | null;
  onClose: () => void;
  /** Given the case's id when one was raised or booked, so the caller can open it. */
  onDone: (orderId?: string) => void;
  /** Opened on an existing open case because one was asked to be raised. */
  continued?: boolean;
}) {
  // Choosing the lab is Operations' decision (docs/37 A5, D7). Somebody
  // without `examination.route` raises the case with a preference and books
  // at the lab Operations assigns; they never pick or change the lab.
  const { permissions } = useAuth();
  const choosesLab = permissions.has('examination.route');
  // The certifying physician is Dr Ultimate's panel, not the company's: HR
  // choosing the lab (docs/38 D5) leaves it blank and one is assigned when
  // the case reaches review. Operations still names one when it routes.
  const choosesPhysician = choosesLab && permissions.has('case.read-ops');
  // Reading the network (a lab's name, branches, capacity) and the package
  // list are permissions of their own. A plant login that may raise and book
  // a case but not browse the network was sent 403s from this dialog; it now
  // books at the lab on the case without being shown the network.
  const readsNetwork = permissions.has('partner.read');
  const [note, setNote] = useState('');
  const [employee, setEmployee] = useState<EmployeeRow | null>(
    given ? { id: given.id, fullName: given.fullName, employeeCode: given.employeeCode, status: given.status } as EmployeeRow
      : order?.employee ? { id: order.employeeId, ...order.employee } as EmployeeRow : null,
  );
  const [type, setType] = useState(
    order?.examinationType ?? (given?.status === 'CANDIDATE' ? 'PRE_EMPLOYMENT' : 'PERIODIC_OHC'),
  );
  // Once somebody picks a type, the suggestion stops moving it.
  const [typeTouched, setTypeTouched] = useState(false);
  const [orderId, setOrderId] = useState<string | null>(order?.id ?? null);
  useEffect(() => {
    if (!orderId && employee?.status === 'CANDIDATE') setType('PRE_EMPLOYMENT');
  }, [employee?.id, employee?.status, orderId]);
  const [routed] = useState<boolean>(
    Boolean(order && order.status !== 'REQUESTED'),
  );
  const [partner, setPartner] = useState<Partner | null>(null);
  const [physician, setPhysician] = useState<PhysicianRow | null>(null);
  const [reason, setReason] = useState('Nearest empanelled centre');
  // The browser's own calendar day: at 01:00 IST the UTC date is still
  // yesterday, and the slot picker opened on a date already past. And the
  // next slot still ahead, not 09:30 today whatever the time (5.2).
  const [first] = useState(firstFreeSlot);
  const [date, setDate] = useState(first.date);
  const [time, setTime] = useState(first.time);
  const [dateTouched, setDateTouched] = useState(false);
  const [locationId, setLocationId] = useState('');
  const [venueId, setVenueId] = useState('');
  const [busy, setBusy] = useState<false | 'raise' | 'book'>(false);
  const [error, setError] = useState<string | null>(null);
  const [issued, setIssued] = useState<string | null>(null);
  const [booked, setBooked] = useState(false);
  const [copied, setCopied] = useState(false);
  const [step, setStep] = useState<StepKey>(() => (order || given ? (order && order.status !== 'REQUESTED' ? 'when' : 'checkup') : 'employee'));
  const [continuing, setContinuing] = useState<ExaminationOrderRow | null>(null);
  const [continueFailed, setContinueFailed] = useState(false);
  const idem = useIdempotencyKey();
  // A routed case already names its lab; somebody who cannot read the
  // network still books there, by the id on the case.
  const partnerId = partner?.id ?? (routed ? order?.assignedPartnerId ?? '' : '');

  // Pre-check on the employee: job role, hazards, protocol, an open order.
  const readiness = useQuery<ExaminationReadiness | null>(
    () => (employee && !orderId
      ? api.get<ExaminationReadiness>(
          `/examinations/readiness/${employee.id}?examinationType=${encodeURIComponent(type)}`,
        )
      : Promise.resolve(null)),
    [employee?.id, orderId, type],
  );
  const r = readiness.data;
  const blocked = !orderId && Boolean(employee) && (!r || !r.ready);

  // The examination type picks a default package; a company with packages of
  // its own may run one of those instead. Only those are offered here.
  const companyPackages = useQuery<{ items: CompanyPackage[] } | null>(
    () => (order ? Promise.resolve(null) : api.get<{ items: CompanyPackage[] }>('/company-packages').catch(() => null)),
    [Boolean(order)],
  );
  const ownPackages = (companyPackages.data?.items ?? []).filter((p) => p.own && p.isActive);
  const [packageId, setPackageId] = useState('');
  const chosenPackage = ownPackages.find((p) => p.id === packageId) ?? null;
  const defaultPackageLabel = `Default for this examination${(r as ReadinessWithPackage | null | undefined)?.package?.name ? ` (${(r as ReadinessWithPackage).package!.name})` : ''}`;
  const packageField = chosenPackage ? { packageId: chosenPackage.id } : {};

  // Start on the type this employee's case most likely is — the company's
  // only configuration is "Pre-employment 2026", or they joined last week —
  // rather than "Periodic" for everybody (5.1).
  useEffect(() => {
    if (!typeTouched && !orderId && r?.suggestedType && r.suggestedType !== type
        && (employee?.status !== 'CANDIDATE' || r.suggestedType === 'PRE_EMPLOYMENT')) {
      setType(r.suggestedType);
    }
  }, [r?.suggestedType, typeTouched, orderId, type, employee?.status]);

  // An open case nobody has booked yet is carried on with, not refused: the
  // only way forward used to be a small "Open that case" link and a second
  // dialog (5.3).
  const openId = !order && !orderId && !continueFailed && r?.openOrder && BOOKABLE.has(r.openOrder.status)
    ? r.openOrder.id : null;
  useEffect(() => {
    if (!openId) return;
    let live = true;
    api.get<ExaminationOrderRow>(`/examinations/${openId}`)
      .then((o) => { if (live) setContinuing(o); })
      // Unreadable (another plant's case): the refusal and its link stay.
      .catch(() => { if (live) setContinueFailed(true); });
    return () => { live = false; };
  }, [openId]);

  // An order that was already routed knows its lab; seed the picker
  // with the record rather than an id nobody can read.
  const assigned = useQuery<Partner | null>(
    () => (order?.assignedPartnerId && readsNetwork ? api.get<Partner>(`/partners/${order.assignedPartnerId}`) : Promise.resolve(null)),
    [order?.assignedPartnerId, readsNetwork],
  );
  useEffect(() => { if (assigned.data && !partner) setPartner(assigned.data); }, [assigned.data, partner]);

  // The physician a lab usually works with, remembered per lab on
  // this browser. Not authoritative — the panel search still applies — but
  // it turns the second-most-typed field into a confirmation.
  useEffect(() => {
    if (!partnerId || physician || routed || !choosesPhysician) return;
    try {
      const raw = localStorage.getItem(`dru.hr.physicianFor.${partnerId}`);
      if (raw) setPhysician(JSON.parse(raw));
    } catch { /* nothing remembered */ }
  }, [partnerId, physician, routed]);

  const branches = useQuery<{ items: PartnerLocation[] }>(
    () => (partnerId && readsNetwork ? api.get(`/partners/${partnerId}/locations`) : Promise.resolve({ items: [] })),
    [partnerId, readsNetwork],
  );
  const campaignId = order?.campaignId ?? null;
  const venues = useQuery<{ items: CampaignVenue[] }>(
    () => (campaignId ? api.get(`/campaigns/${campaignId}/venues`) : Promise.resolve({ items: [] })),
    [campaignId],
  );
  const avail = useQuery<PartnerAvailability | null>(
    () => (partnerId && readsNetwork ? api.get(`/partners/${partnerId}/availability?date=${date}`) : Promise.resolve(null)),
    [partnerId, date, readsNetwork],
  );

  // A day the lab is full moves the untouched default on to the next
  // working day, a fortnight at most; a date somebody chose is left alone.
  const [skipped, setSkipped] = useState(0);
  useEffect(() => {
    if (avail.data?.full && !dateTouched && skipped < 14) {
      setSkipped((n) => n + 1);
      setDate((d) => nextWorkingDay(d));
    }
  }, [avail.data, dateTouched, skipped]);

  // Today keeps only the times still ahead; a choice that has just become
  // the past moves to the first one left.
  const times = timesOn(date);
  useEffect(() => {
    if (times.length && !times.includes(time)) setTime(times[0]!);
  }, [date, time, times.join()]);

  /** Raising with a preference, for somebody who does not choose the lab. */
  const preferring = !choosesLab && !routed;
  const needsPhysician = !routed && choosesPhysician;
  const canRaise = Boolean(employee) && !blocked;
  const canRoute = Boolean(partnerId) && (!needsPhysician || Boolean(physician));
  const problem = slotProblem(date, times.length ? time : '');
  const canBook = canRoute && !avail.data?.full && !problem;
  const plantId = r?.employee.plant?.id ?? order?.plantId ?? null;

  /**
   * Raise → route → book, in one request. `bookNow` false stops after
   * routing ("Book later"). Somebody who does not choose the lab only raises.
   */
  async function go(bookNow: boolean) {
    setError(null);
    if (bookNow) {
      const p = slotProblem(date, times.length ? time : '');
      if (p) { setError(p); return; }
    }
    try {
      if (!choosesLab && !routed) {
        setBusy('raise');
        const created = await api.post<{ id: string }>('/examinations',
          {
            employeeId: employee!.id, examinationType: type, ...packageField,
            // Only a preference when this person does not choose the lab.
            ...(locationId ? { requestedPartnerLocationId: locationId } : {}),
            ...(note.trim() || partner
              ? { requestedLocationNote: [partner && !locationId ? `Prefers ${partner.name}` : '', note.trim()].filter(Boolean).join(' — ') }
              : {}),
          }, idem.key());
        idem.rotate();
        setOrderId(created.id);
        onDone(created.id);
        return;
      }
      setBusy(bookNow ? 'book' : 'raise');
      const res = await api.post<{ orderId: string; checkInToken: string | null }>('/examinations/arrange', {
        ...(orderId ? { orderId } : { raise: { employeeId: employee!.id, examinationType: type, ...packageField } }),
        ...(!routed ? {
          route: {
            partnerId,
            ...(locationId ? { partnerLocationId: locationId } : {}),
            ...(physician ? { physicianId: physician.id } : {}),
            reason,
          },
        } : {}),
        ...(bookNow ? {
          appointment: {
            partnerId,
            scheduledFor: new Date(`${date}T${time}:00`).toISOString(),
            slotMinutes: 30,
            venueType: venueId ? 'CAMP' : 'HOSPITAL',
            ...(locationId ? { partnerLocationId: locationId } : {}),
            ...(venueId ? { campaignVenueId: venueId } : {}),
          },
        } : {}),
      }, idem.key());
      idem.rotate();
      setOrderId(res.orderId);
      if (physician && !routed) {
        try { localStorage.setItem(`dru.hr.physicianFor.${partnerId}`, JSON.stringify(physician)); } catch { /* fine */ }
      }
      if (!bookNow) { onDone(res.orderId); return; }
      setIssued(res.checkInToken ?? null);
      setBooked(true);
    } catch (e) {
      setError(errorMessage(e));
    } finally { setBusy(false); }
  }

  if (continuing) {
    return (
      <ArrangeDialog
        employee={given ?? (employee ? { id: employee.id, fullName: employee.fullName, employeeCode: employee.employeeCode, status: employee.status } : null)}
        order={continuing}
        onClose={onClose}
        onDone={onDone}
        continued
      />
    );
  }

  if (booked) {
    const code = employee?.employeeCode ?? order?.employee?.employeeCode;
    return (
      <Dialog title="Appointment booked" onClose={() => onDone(orderId ?? undefined)}>
        <p className="mt-2 text-sm text-neutral-700">
          {employee?.fullName ?? 'The employee'} is booked{partner?.name ? ` at ${partner.name}` : ''} on{' '}
          {new Date(`${date}T${time}:00`).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}.
        </p>
        {/* How the employee gets in, said to the person who will be asked.
            The check-in pass used to be the only thing on this screen, shown
            once and never sent to anybody (flow audit 2026-10-20, 5.5). */}
        <div className="mt-3 rounded border border-neutral-200 bg-neutral-50 p-3 text-sm text-neutral-700">
          <p className="font-medium text-neutral-900">How they check in</p>
          <p className="mt-1">
            At the lab's desk with their employee code{code ? <> <span className="font-mono text-neutral-900">{code}</span></> : ''} and
            a photo ID. The employee has been sent the time, the place and their employee code, and can sign in to the employee app with their mobile number.
          </p>
        </div>
        {issued && (
          <details className="mt-3 rounded border border-neutral-200 p-3">
            <summary className="flex cursor-pointer items-center gap-1 text-xs font-medium text-neutral-700">
              Check-in pass (optional)
              <InfoTip label="About the check-in pass">
                A stronger identity check than the employee code. If you pass it on, the desk scans or types it
                instead. It is shown only here, once — nothing else stores it.
              </InfoTip>
            </summary>
            <div className="mt-2 flex items-center gap-2">
              <p className="min-w-0 flex-1 break-all font-mono text-sm text-neutral-900">{issued}</p>
              <Button size="sm" variant="secondary" onClick={() => {
                navigator.clipboard?.writeText(issued).then(() => setCopied(true)).catch(() => undefined);
              }}>
                {copied ? 'Copied' : 'Copy'}
              </Button>
            </div>
          </details>
        )}
        <div className="mt-5 flex justify-end">
          <Button onClick={() => onDone(orderId ?? undefined)}>Done</Button>
        </div>
      </Dialog>
    );
  }

  const title = preferring
    ? (order ? 'Waiting for a lab' : 'Schedule a checkup')
    : order
      ? (routed ? 'Book a slot' : 'Choose a lab and book')
      : 'Schedule a checkup';

  // A case that is waiting for Operations has nothing to choose yet.
  if (preferring && order) {
    return (
      <Dialog title={title} onClose={onClose}>
        <EmployeeLine employee={employee} order={order} plant={r?.employee.plant?.name} />
        <p className="mt-3 rounded bg-neutral-50 px-3 py-2 text-small text-neutral-700">
          {BRAND.name} Operations assigns the lab for this medical case. Once they have, you can
          book the slot here.
        </p>
        <div className="mt-5 flex justify-end">
          <Button variant="secondary" onClick={onClose}>Close</Button>
        </div>
      </Dialog>
    );
  }

  // ── The steps ─────────────────────────────────────────────
  // Employee → checkup & venue → date & time → confirm. A step with nothing
  // to decide is left out: the employee when the dialog was opened for one,
  // the venue on a case whose lab is already assigned (unless it can be run
  // at a camp), the slot for somebody who only raises with a preference.
  const hasVenues = (venues.data?.items.length ?? 0) > 0;
  const stepKeys: StepKey[] = [
    ...(order || given ? [] : ['employee' as const]),
    ...(!routed || hasVenues ? ['checkup' as const] : []),
    ...(!preferring ? ['when' as const] : []),
    'confirm',
  ];
  const current: StepKey = stepKeys.includes(step) ? step : stepKeys[0]!;
  const at = stepKeys.indexOf(current);
  const back = at > 0 ? () => { setError(null); setStep(stepKeys[at - 1]!); } : null;
  const next = () => { setError(null); setStep(stepKeys[at + 1]!); };

  const checkingReadiness = Boolean(employee) && !orderId && readiness.loading;
  const canLeave: Record<StepKey, boolean> = {
    employee: Boolean(employee) && !checkingReadiness && !blocked && !openId,
    checkup: canRaise && !checkingReadiness && (preferring || canRoute),
    when: canBook,
    confirm: true,
  };

  const slotLabel = new Date(`${date}T${time}:00`).toLocaleString([], { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' });
  const packageName = chosenPackage?.name ?? (r as ReadinessWithPackage | null | undefined)?.package?.name ?? r?.configuration?.name ?? null;
  const testCount = chosenPackage ? chosenPackage.tests.length : r?.tests.length ?? null;
  const branch = otherBranches(branches.data?.items).find((b) => b.id === locationId) ?? null;
  const venue = venues.data?.items.find((v) => v.id === venueId) ?? null;

  return (
    <Dialog title={title} onClose={busy ? () => undefined : onClose} wide>
      <StepBar keys={stepKeys} current={current} preferring={preferring} />

      {current !== 'employee' && (
        <EmployeeLine employee={employee} order={order} plant={r?.employee.plant?.name} />
      )}
      {continued && (
        <p className="mt-2 rounded bg-neutral-50 px-3 py-2 text-xs text-neutral-700">
          This employee already has this medical case open, not yet booked. {preferring ? 'It continues here.' : 'Book it here.'}
        </p>
      )}

      {/* ── 1 · Employee ───────────────────────────────────── */}
      {current === 'employee' && (
        <div className="mt-4 space-y-3">
          <EntityCombobox<EmployeeRow>
            label="Who needs a checkup?"
            required
            value={employee}
            onChange={(e) => { setEmployee(e); setOrderId(null); setTypeTouched(false); }}
            fetchOptions={async (search, cursor) => {
              const page = await api.get<CursorPage<EmployeeRow>>(
                `/employees?limit=20&q=${encodeURIComponent(search)}` + (cursor ? `&cursor=${cursor}` : ''));
              return { items: page.items, nextCursor: page.nextCursor };
            }}
            getKey={(e) => e.id}
            placeholder="Search for an employee…"
            searchPlaceholder="Name, employee ID, mobile or email"
            emptyMessage="No employee matches that. Check the spelling, or the employee ID."
            renderOption={(e) => (
              <>
                <span className="block font-medium text-neutral-900">{e.fullName}</span>
                <span className="block font-mono text-caption font-normal text-neutral-500">
                  {e.employeeCode}{e.status !== 'ACTIVE' && ` · ${e.status.toLowerCase().replace(/_/g, ' ')}`}
                </span>
              </>
            )}
            renderValue={(e) => `${e.fullName} — ${e.employeeCode}`}
          />
          {checkingReadiness && <p className="text-xs text-neutral-500">Checking this employee…</p>}
          {employee && !orderId && readiness.error && <Alert>{readiness.error}</Alert>}
          {employee && !orderId && openId && !continuing && (
            <p className="text-xs text-neutral-500">This employee has an open medical case. Opening it…</p>
          )}
          {employee && !orderId && r && !r.ready && <ReadinessSummary r={r} />}
          {employee && !orderId && r?.ready && (
            <p className="flex items-center gap-1.5 text-small text-covered-600">
              <CircleCheck aria-hidden className="h-4 w-4" />
              {r.employee.fullName}{r.employee.plant ? `, ${r.employee.plant.name}` : ''}, can be scheduled.
            </p>
          )}
        </div>
      )}

      {/* ── 2 · Checkup & venue ─────────────────────────────── */}
      {current === 'checkup' && (
        <div className="mt-4 space-y-4">
          {!order && (
            <label className="block">
              <span className="text-xs font-medium text-neutral-700">Checkup type</span>
              <Select value={type} onChange={(e) => { setType(e.target.value); setTypeTouched(true); }} className="mt-1" disabled={Boolean(orderId)}>
                {EXAM_TYPES
                  .filter(([v]) => employee?.status !== 'CANDIDATE' || v === 'PRE_EMPLOYMENT')
                  .map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </Select>
            </label>
          )}

          {!order && ownPackages.length > 0 && (
            <SearchSelect
              label="Package"
              value={packageId}
              onChange={setPackageId}
              disabled={Boolean(orderId)}
              searchPlaceholder="Search packages by name or code…"
              placeholder={defaultPackageLabel}
              options={[
                { value: '', label: defaultPackageLabel },
                ...ownPackages.map((p) => ({
                  value: p.id,
                  label: p.name,
                  hint: `${p.code} · ${p.tests.length} test${p.tests.length === 1 ? '' : 's'}`,
                })),
              ]}
            />
          )}

          {checkingReadiness && <p className="text-xs text-neutral-500">Checking what this checkup needs…</p>}
          {employee && !orderId && r && !r.ready && <ReadinessSummary r={r} />}
          {employee && !orderId && r?.ready && (
            <>
              <Notices r={r} />
              <PackageNote name={packageName} tests={chosenPackage ? chosenPackage.tests : r.tests} periodicityMonths={chosenPackage ? null : r.periodicityMonths} />
            </>
          )}

          {(!employee || !blocked) && (
            <div className="space-y-3 border-t border-neutral-100 pt-4">
              {preferring && (
                <p className="flex flex-wrap items-center gap-1 text-xs text-neutral-600">
                  {BRAND.name} Operations chooses the lab.
                  <InfoTip label="About choosing the lab">
                    Capability, capacity and distance decide.
                    {readsNetwork ? ' Your preferred lab is taken into account.' : ' You book the slot once they have.'}
                  </InfoTip>
                </p>
              )}
              {readsNetwork ? (
                <>
                  <HospitalPicker
                    disabled={routed}
                    value={partner}
                    onChange={(p) => { setPartner(p); setPhysician(null); setLocationId(''); }}
                    label={preferring ? 'Preferred lab (optional)' : 'Lab'}
                    required={!preferring}
                    placeholder={plantId ? 'Choose a lab — nearest first' : 'Search empanelled labs…'}
                    searchPlaceholder="Lab name, code or town"
                    nearPlantId={plantId}
                  />
                  {partner && <LabCard partner={partner} />}
                </>
              ) : routed ? (
                <p className="text-small text-neutral-700">
                  <span className="font-medium text-neutral-900">Lab:</span> the one Operations assigned to this case.
                  The employee is told where to go when the slot is booked.
                </p>
              ) : null}

              {needsPhysician && (
                <EntityCombobox<PhysicianRow>
                  label="Certifying physician"
                  required
                  value={physician}
                  onChange={setPhysician}
                  fetchOptions={searchPhysicians}
                  getKey={(p) => p.id}
                  placeholder="Search the certifying panel…"
                  info={<>Only the assigned physician can certify. The last one used for this lab is suggested; change it if somebody else is covering.</>}
                  searchPlaceholder="Name or registration number"
                  emptyMessage="No AFIH-certified physician matches that."
                  renderOption={(p) => (
                    <>
                      <span className="block font-medium text-neutral-900">{p.fullName}</span>
                      <span className="block font-mono text-caption font-normal text-neutral-500">
                        {p.registrationNo}{p.linked ? '' : ' · no login yet'}
                      </span>
                    </>
                  )}
                  renderValue={(p) => `${p.fullName} — ${p.registrationNo}`}
                />
              )}
              {needsPhysician && (
                <label className="block">
                  <span className="text-xs font-medium text-neutral-700">Routing reason</span>
                  <Input value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1" />
                </label>
              )}

              {!routed && otherBranches(branches.data?.items).length > 0 && (
                <SearchSelect label={preferring ? 'Preferred branch' : 'Branch'} value={locationId} onChange={setLocationId}
                  info="This lab has more than one site. The employee is told to go to the one chosen here."
                  placeholder="Registered site (the lab's own address)" searchPlaceholder="Search branches by name or city…"
                  options={[
                    { value: '', label: "Registered site (the lab's own address)" },
                    ...otherBranches(branches.data?.items).map((b) => ({ value: b.id, label: b.name, hint: b.city ?? undefined })),
                  ]} />
              )}
              {!preferring && hasVenues && (
                <SearchSelect label="Camp venue" value={venueId} onChange={setVenueId}
                  info="Booked as a camp visit at this venue; the lab above runs it."
                  placeholder="Not at a camp — at the lab" searchPlaceholder="Search venues by name or city…"
                  options={[
                    { value: '', label: 'Not at a camp — at the lab' },
                    ...venues.data!.items.map((v) => ({
                      value: v.id,
                      label: v.name,
                      hint: [v.city, v.startsOn ? `${v.startsOn.slice(0, 10)}${v.endsOn ? ` → ${v.endsOn.slice(0, 10)}` : ''}` : null].filter(Boolean).join(' · ') || undefined,
                    })),
                  ]} />
              )}
              {preferring && (
                <label className="block">
                  <span className="text-xs font-medium text-neutral-700">Note for Operations <span className="font-normal text-neutral-500">(optional)</span></span>
                  <Input value={note} onChange={(e) => setNote(e.target.value)} className="mt-1"
                    placeholder="e.g. employee lives near the Vapi gate; night shift" />
                </label>
              )}
              {!needsPhysician && (
                <p className="text-xs text-neutral-500">The certifying physician is assigned by {BRAND.name}.</p>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── 3 · Date & time ─────────────────────────────────── */}
      {current === 'when' && (
        <SlotPicker
          date={date}
          time={time}
          times={times}
          onDate={(d) => { setDate(d); setDateTouched(true); }}
          onTime={setTime}
          problem={problem}
          avail={avail.data}
          availLoading={avail.loading && Boolean(partnerId) && readsNetwork}
        />
      )}

      {/* ── 4 · Confirm ─────────────────────────────────────── */}
      {current === 'confirm' && (
        <div className="mt-4">
          <p className="text-small font-semibold text-neutral-900">Check the booking</p>
          <dl className="mt-2 divide-y divide-neutral-100 rounded-card border border-neutral-200 text-small">
            <SummaryRow label="Employee">
              {employee?.fullName} <span className="font-mono text-caption text-neutral-500">{employee?.employeeCode}</span>
              {r?.employee.plant && <span className="block text-caption text-neutral-500">{r.employee.plant.name}</span>}
            </SummaryRow>
            <SummaryRow label="Checkup">
              {order
                ? (EXAM_TYPE_LABEL[order.examinationType] ?? order.examinationType)
                : (EXAM_TYPES.find(([v]) => v === type)?.[1] ?? EXAM_TYPE_LABEL[type] ?? type)}
              {packageName && (
                <span className="block text-caption text-neutral-500">
                  {packageName}{testCount != null ? ` · ${testCount} test${testCount === 1 ? '' : 's'}` : ''}
                </span>
              )}
            </SummaryRow>
            <SummaryRow label="Lab">
              {preferring
                ? <>Assigned by {BRAND.name} Operations{partner ? <span className="block text-caption text-neutral-500">Your preference: {partner.name}{branch ? `, ${branch.name}` : ''}</span> : null}</>
                : partner
                  ? <>{partner.name}<span className="block text-caption text-neutral-500">{[branch?.name, venue ? `Camp: ${venue.name}` : null, addressOf(partner)].filter(Boolean).join(' · ')}</span></>
                  : 'The lab assigned to this case'}
            </SummaryRow>
            {!preferring && <SummaryRow label="Date & time">{slotLabel}</SummaryRow>}
            <SummaryRow label="Physician">
              {physician ? `${physician.fullName} — ${physician.registrationNo}` : `Assigned by ${BRAND.name}`}
            </SummaryRow>
            {r?.requiresConsent != null && !orderId && (
              <SummaryRow label="Consent">{r.requiresConsent ? 'Taken from the employee before check-in' : 'Not required'}</SummaryRow>
            )}
          </dl>
          <p className="mt-3 text-xs text-neutral-600">
            {preferring
              ? 'The medical case is raised now. You book the slot once Operations has assigned a lab.'
              : 'The employee is sent the time, place and their employee ID as soon as you confirm.'}
          </p>
        </div>
      )}

      {error && <Alert tone="danger" className="mt-3">{error}</Alert>}

      <div className="mt-5 flex items-center justify-between gap-2 border-t border-neutral-100 pt-4 max-sm:flex-col-reverse max-sm:items-stretch">
        {back
          ? <Button variant="ghost" onClick={back} disabled={Boolean(busy)}>Back</Button>
          : <Button variant="ghost" onClick={onClose} disabled={Boolean(busy)}>Cancel</Button>}
        <div className="flex flex-wrap justify-end gap-2 max-sm:[&>*]:flex-1">
          {current !== 'confirm' ? (
            <Button onClick={next} disabled={!canLeave[current]}>Continue</Button>
          ) : preferring ? (
            <Button onClick={() => go(false)} loading={busy === 'raise'}
              disabled={Boolean(busy) || !canRaise || Boolean(orderId) || readiness.loading}>
              Raise medical case
            </Button>
          ) : (
            <>
              {!routed && (
                <Button variant="secondary" onClick={() => go(false)} loading={busy === 'raise'}
                  disabled={Boolean(busy) || !canRaise || !canRoute}
                  title="Raise and assign the lab now; book the slot later from Medical cases">
                  Book later
                </Button>
              )}
              <Button onClick={() => go(true)} loading={busy === 'book'}
                disabled={Boolean(busy) || !canRaise || !canBook || readiness.loading}>
                {busy === 'book' ? 'Booking…' : 'Confirm booking'}
              </Button>
            </>
          )}
        </div>
      </div>
    </Dialog>
  );
}

type StepKey = 'employee' | 'checkup' | 'when' | 'confirm';

const STEP_LABEL: Record<StepKey, string> = {
  employee: 'Employee',
  checkup: 'Checkup & venue',
  when: 'Date & time',
  confirm: 'Confirm',
};

/** Where the booking is: the full row from `md`, one line on a phone. */
function StepBar({ keys, current, preferring }: { keys: StepKey[]; current: StepKey; preferring: boolean }) {
  if (keys.length < 2) return null;
  const at = keys.indexOf(current);
  const label = (k: StepKey) => (k === 'checkup' && preferring ? 'Checkup & lab preference' : STEP_LABEL[k]);
  return (
    <div className="mt-3">
      <p className="text-caption font-medium text-neutral-500 md:hidden">
        Step {at + 1} of {keys.length} · <span className="text-neutral-900">{label(current)}</span>
      </p>
      <Steps
        className="max-md:hidden"
        orientation="horizontal"
        label="Booking steps"
        steps={keys.map((k, i) => ({
          key: k, label: label(k),
          state: i < at ? 'done' : i === at ? 'current' : 'upcoming',
        }))}
      />
    </div>
  );
}

/** Who the booking is for, once chosen. */
function EmployeeLine({ employee, order, plant }: {
  employee: EmployeeRow | null; order?: ExaminationOrderRow | null; plant?: string | null;
}) {
  if (!employee) return null;
  return (
    <p className="mt-3 text-sm text-neutral-800">
      <span className="font-medium">{employee.fullName}</span>{' '}
      <span className="font-mono text-xs text-neutral-500">{employee.employeeCode}</span>
      {plant && <span className="text-xs text-neutral-500"> · {plant}</span>}
      {order && <span className="text-xs text-neutral-500"> · {(EXAM_TYPE_LABEL[order.examinationType] ?? order.examinationType)}</span>}
    </p>
  );
}

/** The package the checkup runs, said plainly; the test list is there for whoever asks. */
function PackageNote({ name, tests, periodicityMonths }: {
  name: string | null; tests: Array<{ name: string }>; periodicityMonths?: number | null;
}) {
  return (
    <div className="rounded-card border border-neutral-200 bg-neutral-50 px-3 py-2 text-small">
      <p className="text-neutral-900">
        <span className="font-medium">{name ?? 'Company package'}</span>
        <span className="text-neutral-600"> · {tests.length} test{tests.length === 1 ? '' : 's'}{periodicityMonths ? ` · every ${periodicityMonths} months` : ''}</span>
      </p>
      <p className="mt-0.5 text-caption text-neutral-500">Set up by {BRAND.name} for this checkup. Nothing to choose.</p>
      {tests.length > 0 && (
        <details className="mt-1">
          <summary className="cursor-pointer text-caption font-medium text-primary-700">See the tests</summary>
          <p className="mt-1 text-caption text-neutral-600">{tests.map((t) => t.name).join(', ')}</p>
        </details>
      )}
    </div>
  );
}

/** Advisory notices before raising: a recent case of the same kind, and the like. */
function Notices({ r }: { r: ExaminationReadiness }) {
  if (!(r.notices?.length)) return null;
  return (
    <Alert tone="warning" title="Before you continue">
      {r.notices!.map((n) => (
        <span key={n.code} className="block">
          {n.message}{' '}
          {n.orderId && <Link to={`/hr/examinations/${n.orderId}`} className="font-medium underline">Open that case</Link>}
        </span>
      ))}
    </Alert>
  );
}

const addressOf = (p: Partner) => [p.locality, p.city, p.pincode].filter(Boolean).join(', ');

/** The chosen lab: where it is and how far, so the choice is made with the address in view. */
function LabCard({ partner: p }: { partner: Partner }) {
  return (
    <div className="flex items-start gap-2 rounded-card border border-neutral-200 px-3 py-2 text-small">
      <MapPin aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-primary-600" />
      <div className="min-w-0">
        <p className="font-medium text-neutral-900">{p.name}{p.preferred && <span className="ml-1.5 rounded bg-primary-50 px-1.5 py-0.5 text-[10px] font-medium text-primary-700">preferred</span>}</p>
        <p className="text-caption text-neutral-600">
          {[addressOf(p) || null, p.distanceKm != null ? `≈ ${p.distanceKm} km from the plant` : null].filter(Boolean).join(' · ') || 'Address on the lab record'}
        </p>
        <p className="text-caption text-neutral-500">{p.testsOffered} tests offered · up to {p.dailyCapacity} visits a day</p>
      </div>
    </div>
  );
}

function SummaryRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[7rem_1fr] gap-3 px-3 py-2 max-sm:grid-cols-1 max-sm:gap-0.5">
      <dt className="text-neutral-500">{label}</dt>
      <dd className="min-w-0 text-neutral-900">{children}</dd>
    </div>
  );
}

const PART_OF_DAY: Array<[string, string, string]> = [
  ['Morning', '00:00', '12:00'],
  ['Afternoon', '12:00', '16:00'],
  ['Evening', '16:00', '24:00'],
];

/** Days to offer as one tap: today if it has room, then the next few working days. */
function quickDays(): string[] {
  const out: string[] = [];
  let d = localDay();
  if (timesOn(d).length && new Date().getDay() !== 0) out.push(d);
  while (out.length < 4) { d = nextWorkingDay(d); out.push(d); }
  return out;
}

/**
 * The date and the time, as taps rather than two dropdowns. Times every half
 * hour, grouped by part of day; the day's remaining capacity at the lab is
 * said under the date (capacity is per day — the API has no per-slot count).
 */
function SlotPicker({ date, time, times, onDate, onTime, problem, avail, availLoading }: {
  date: string; time: string; times: string[];
  onDate: (d: string) => void; onTime: (t: string) => void;
  problem: string | null; avail?: PartnerAvailability | null; availLoading: boolean;
}) {
  const [days] = useState(quickDays);
  const today = localDay();
  const label = (d: string) => {
    if (d === today) return 'Today';
    const dt = new Date(`${d}T00:00:00`);
    return dt.toLocaleDateString([], { weekday: 'short', day: 'numeric', month: 'short' });
  };
  // Half-hour slots, plus whatever is chosen now (a quarter-hour start today).
  const shown = times.filter((t) => t.endsWith(':00') || t.endsWith(':30') || t === time);
  return (
    <div className="mt-4 space-y-4">
      <div>
        <p className="text-xs font-medium text-neutral-700">Date</p>
        <div className="mt-1 flex flex-wrap items-center gap-2">
          {days.map((d) => (
            <Chip key={d} selected={d === date} onClick={() => onDate(d)}>{label(d)}</Chip>
          ))}
          <label className="inline-flex items-center gap-2 text-small text-neutral-600">
            <span className="sr-only">Another date</span>
            <Input type="date" value={date} min={today} onChange={(e) => e.target.value && onDate(e.target.value)} className="h-10 w-auto py-0" />
          </label>
        </div>
        {availLoading ? (
          <p className="mt-2 text-xs text-neutral-500">Checking the lab's capacity…</p>
        ) : avail && (
          <p className={cn('mt-2 inline-flex items-center gap-1.5 text-xs', avail.full ? 'text-overdue-600' : 'text-covered-600')}>
            <span aria-hidden className={cn('h-2 w-2 rounded-full', avail.full ? 'bg-overdue-600' : 'bg-covered-600')} />
            {avail.full
              ? `${avail.partnerName} is full on this date (${avail.booked} of ${avail.dailyCapacity}). Choose another day.`
              : `${avail.remaining} of ${avail.dailyCapacity} visits still free on this date.`}
          </p>
        )}
      </div>

      <div>
        <p className="text-xs font-medium text-neutral-700">Time</p>
        {!times.length ? (
          <p className="mt-1 text-xs text-overdue-600">No times are left today. Choose another day.</p>
        ) : (
          <div className="mt-1 space-y-2">
            {PART_OF_DAY.map(([name, fromT, toT]) => {
              const list = shown.filter((t) => t >= fromT && t < toT);
              if (!list.length) return null;
              return (
                <div key={name} role="group" aria-label={name}>
                  <p className="text-caption text-neutral-500">{name}</p>
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    {list.map((t) => (
                      <Chip key={t} selected={t === time} onClick={() => onTime(t)} small>{t}</Chip>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {problem && times.length > 0 && <p className="mt-2 text-xs text-overdue-600">{problem}</p>}
      </div>
    </div>
  );
}

function Chip({ selected, onClick, children, small }: {
  selected: boolean; onClick: () => void; children: ReactNode; small?: boolean;
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onClick}
      className={cn(
        'inline-flex items-center justify-center rounded-btn border font-medium tabular-nums transition-colors duration-hover',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1',
        small ? 'h-9 min-w-[4.25rem] px-2.5 text-small max-md:h-10' : 'h-10 px-3 text-small',
        selected
          ? 'border-primary-600 bg-primary-600 text-white'
          : 'border-neutral-300 bg-white text-neutral-800 hover:border-primary-300 hover:bg-primary-50',
      )}
    >
      {children}
    </button>
  );
}

/**
 * What the examination will be, or what stands in its way.
 *
 * The refusal used to read "no test battery could be derived" — an
 * architectural fact about protocol resolution that told HR nothing about
 * what to click. This lists the missing pieces in the order they are
 * fixed, with a link to the screen that fixes each.
 */
/** Readiness as the API now sends it: with the package the type picks. */
type ReadinessWithPackage = ExaminationReadiness & { package?: { id: string; name: string } | null };

export function ReadinessSummary({ r, chosen = null, labAndSlot = 'Chosen below' }: {
  r: ExaminationReadiness;
  /** A company package HR picked instead of the default for the type. */
  chosen?: CompanyPackage | null;
  /** What the dialog around it does about the lab and the slot. */
  labAndSlot?: string;
}) {
  if (!r.ready) {
    // Nothing here is the customer's to fix by configuring: a missing
    // medical package is Dr Ultimate's to set up (docs/38), so the message
    // says who to call and offers no setup screen.
    return (
      <div className="mt-3 rounded-card border border-warning-600/30 bg-warning-100 p-3">
        <p className="text-sm font-semibold text-warning-600">This medical case cannot be raised yet.</p>
        {r.missing.length > 0 && (
          <p className="mt-1 text-xs text-neutral-700">{r.missing[0]!.message}</p>
        )}
        {r.blockers.map((b) => (
          <p key={b.code} className="mt-1 text-xs text-neutral-700">
            {b.message}{' '}
            {b.orderId && (
              <Link to={`/hr/examinations/${b.orderId}`} className="font-medium underline">Open that case</Link>
            )}
          </p>
        ))}
      </div>
    );
  }
  return (
    <>
    {(r.notices?.length ?? 0) > 0 && (
      // Said, not enforced: a finished case blocks nothing, but a second one
      // of the same kind is a second visit and a second invoice, and the
      // person raising it should know the first exists.
      <Alert tone="warning" className="mt-3" title="Before you raise this">
        {r.notices!.map((n) => (
          <span key={n.code} className="block">
            {n.message}{' '}
            {n.orderId && (
              <Link to={`/hr/examinations/${n.orderId}`} className="font-medium underline">Open that case</Link>
            )}
          </span>
        ))}
      </Alert>
    )}
    <div className="mt-3 rounded-card border border-neutral-200 bg-neutral-50 p-3 text-xs">
      <p className="text-sm font-semibold text-neutral-900">What will be raised</p>
      <dl className="mt-2 grid gap-x-4 gap-y-1 sm:grid-cols-[auto_1fr]">
        <dt className="text-neutral-500">Employee</dt>
        <dd className="text-neutral-900">{r.employee.fullName} · <span className="font-mono">{r.employee.employeeCode}</span>{r.employee.plant ? ` · ${r.employee.plant.name}` : ''}</dd>
        <dt className="text-neutral-500">Medical package</dt>
        <dd className="text-neutral-900">
          {/* The tests come from the job role's protocols only for a worker
              who already had a role recorded; everybody else is screened
              under the company's package (docs/38 D4). */}
          {chosen?.name ?? (r as ReadinessWithPackage).package?.name ?? r.configuration?.name ?? 'Company package'}
          {r.source === 'PROTOCOL' && r.protocols.length > 0 && (
            <span className="block text-neutral-600">
              plus {r.protocols.map((p) => p.name).join(', ')} for the job role {r.jobRole?.title ?? ''}
            </span>
          )}
        </dd>
        <dt className="text-neutral-500">Tests</dt>
        <dd className="text-neutral-900">
          {chosen ? (
            <>
              {chosen.tests.length} test{chosen.tests.length === 1 ? '' : 's'}{r.source === 'PROTOCOL' ? ', plus the job role\u2019s' : ''}
              <span className="block text-neutral-600">{chosen.tests.map((t) => t.name).join(', ')}</span>
            </>
          ) : (
            <>
              {r.tests.length} test{r.tests.length === 1 ? '' : 's'}{r.periodicityMonths ? ` · every ${r.periodicityMonths} months` : ''}
              <span className="block text-neutral-600">{r.tests.map((t) => t.name).join(', ')}</span>
            </>
          )}
        </dd>
        <dt className="text-neutral-500">Consent</dt>
        <dd className="text-neutral-900">{r.requiresConsent ? 'Required before check-in' : 'Not required'}</dd>
        <dt className="text-neutral-500">Lab &amp; slot</dt>
        <dd className="text-neutral-900">{labAndSlot}</dd>
      </dl>
    </div>
    </>
  );
}

/**
 * The two network pickers this screen uses.
 *
 * Defined once rather than inline at each call site: routing and scheduling
 * both choose a hospital, and a selector that searches differently depending
 * on which dialog opened it is the kind of inconsistency nobody reports and
 * everybody works around.
 */
export const searchHospitals = async (
  search: string,
  cursor: string | null,
  /// Optional, so every existing call site keeps working unchanged.
  filters?: { place?: string; capability?: string | null; nearPlantId?: string | null },
) => {
  const page = await api.get<CursorPage<Partner>>(
    `/partners?type=HOSPITAL&limit=20&q=${encodeURIComponent(search)}`
    + (filters?.place ?? '')
    + (filters?.capability ? `&capability=${filters.capability}` : '')
    // Nearest first, with the distance, from the employee's plant (flow
    // audit 2026-10-20, 5.4). One page: a distance order has no name cursor.
    + (filters?.nearPlantId ? `&near=${filters.nearPlantId}` : '')
    + (cursor ? `&cursor=${cursor}` : ''),
  );
  return { items: page.items, nextCursor: page.nextCursor };
};

/**
 * AFIH-certified only, which is what the directory returns by default.
 *
 * Anyone else is refused at the moment of certification — so listing them
 * here would offer a choice that fails hours later, on a worker who has
 * already been examined.
 */
export const searchPhysicians = async (search: string, cursor: string | null) => {
  const page = await api.get<CursorPage<PhysicianRow>>(
    `/physicians?limit=20&q=${encodeURIComponent(search)}`
    + (cursor ? `&cursor=${cursor}` : ''),
  );
  return { items: page.items, nextCursor: page.nextCursor };
};

/**
 * A venue in a picker, with enough to tell two of them apart.
 *
 * Name and a test count could not separate "Apollo Hospital, Pune" from
 * "Apollo Hospital, Surat" — the town is the deciding fact, and the daily
 * capacity is the one that decides whether the booking will be accepted at
 * all.
 */
/**
 * The hospital selector, with the filters that make a network of two hundred
 * venues answerable.
 *
 * The search box alone answers "what is this hospital called", which is the
 * question somebody has when they already know the answer. The two filters
 * here are the questions they actually arrive with: "who is near this plant"
 * and "who can run audiometry". Both are server-side — the list is never
 * pulled down and filtered in the browser — and both are optional, so the
 * picker behaves exactly as before until one is used.
 *
 * There is deliberately no availability filter. Availability is per venue per
 * day, and a list filtered on "has room" would be answering for one date
 * while the booking below picks another. The remaining capacity is shown for
 * the chosen venue and date instead, where it is true.
 */
export function HospitalPicker({
  value, onChange, disabled, label = 'Hospital', required = true,
  placeholder = 'Search empanelled hospitals…', searchPlaceholder = 'Hospital name or code', nearPlantId = null,
}: {
  value: Partner | null;
  onChange: (p: Partner | null) => void;
  disabled?: boolean;
  label?: string;
  required?: boolean;
  placeholder?: string;
  searchPlaceholder?: string;
  /** Lists the network nearest this plant first, with distances. */
  nearPlantId?: string | null;
}) {
  const [open, setOpen] = useState(false);
  const [place, setPlace] = useState('');
  const [test, setTest] = useState<CatalogueTest | null>(null);

  // A six-digit entry is a pincode and is matched exactly; anything else is a
  // town and is matched loosely. Typing "396191" into a box labelled "Town or
  // pincode" should not search for a town called 396191.
  const trimmed = place.trim();
  const placeParam = /^[0-9]{6}$/.test(trimmed)
    ? `&pincode=${trimmed}`
    : trimmed ? `&city=${encodeURIComponent(trimmed)}` : '';
  const testId = test?.id ?? null;

  const fetchOptions = useCallback(
    (search: string, cursor: string | null) => searchHospitals(search, cursor, {
      place: placeParam, capability: testId, nearPlantId,
    }),
    [placeParam, testId, nearPlantId],
  );

  const active = (trimmed ? 1 : 0) + (testId ? 1 : 0);

  return (
    <div>
      <EntityCombobox<Partner>
        label={label}
        required={required}
        disabled={disabled}
        value={value}
        onChange={onChange}
        fetchOptions={fetchOptions}
        // Re-runs the search when a filter changes, so the open list is never
        // showing results for filters that are no longer set.
        deps={[placeParam, testId, nearPlantId]}
        getKey={(p) => p.id}
        placeholder={placeholder}
        searchPlaceholder={searchPlaceholder}
        emptyMessage={active
          ? 'No empanelled hospital matches that with these filters.'
          : 'No empanelled hospital matches that.'}
        renderOption={hospitalOption}
        renderValue={(p) => `${p.name}${p.city ? ` — ${p.city}` : ''}`}
      />

      {!disabled && (
        <div className="mt-1">
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            className="text-caption font-medium text-primary-700 hover:underline"
          >
            {open ? 'Hide filters' : active ? `Filters · ${active} applied` : 'Filter by town or test'}
          </button>

          {open && (
            <div className="mt-2 space-y-2 rounded border border-neutral-200 bg-neutral-50 p-3">
              <TextField
                label="Town or pincode"
                value={place}
                onChange={setPlace}
                placeholder="Vapi, or 396191"
              />
              <EntityCombobox<CatalogueTest>
                label="Must be able to run"
                value={test}
                onChange={setTest}
                fetchOptions={searchTests}
                getKey={(t) => t.id}
                placeholder="Any test"
                searchPlaceholder="Test name or code"
                emptyMessage="No test matches that."
                renderOption={(t) => (
                  <>
                    <span className="block font-medium text-neutral-900">{t.name}</span>
                    <span className="block font-mono text-caption font-normal text-neutral-500">
                      {t.code} · {t.category}
                    </span>
                  </>
                )}
                renderValue={(t) => t.name}
              />
              {active > 0 && (
                <button
                  type="button"
                  onClick={() => { setPlace(''); setTest(null); }}
                  className="text-caption font-medium text-neutral-600 hover:underline"
                >
                  Clear filters
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** The catalogue, searched on the server — the picker never holds all of it. */
export const searchTests = async (search: string, cursor: string | null) => {
  const page = await api.get<CursorPage<CatalogueTest>>(
    `/tests?brief=true&limit=20&q=${encodeURIComponent(search)}`
    + (cursor ? `&cursor=${cursor}` : ''),
  );
  return { items: page.items, nextCursor: page.nextCursor };
};

export const hospitalOption = (p: Partner) => (
  <>
    <span className="block font-medium text-neutral-900">
      {p.name}
      {/* The company's own shortlist, marked rather than segregated: the
          server already sorts these to the top of the page, and a separate
          "preferred" group would hide the rest behind a heading on the day
          the first choice is full. */}
      {p.preferred && (
        <span className="ml-1.5 rounded bg-primary-50 px-1.5 py-0.5 text-[10px] font-medium text-primary-700">
          preferred
        </span>
      )}
    </span>
    <span className="block text-caption font-normal text-neutral-500">
      {[
        // Straight-line and approximate, and said so: road distance is longer.
        p.distanceKm != null ? `≈ ${p.distanceKm} km away` : null,
        [p.locality, p.city].filter(Boolean).join(', ') || p.city,
        `${p.testsOffered} tests`,
        `${p.dailyCapacity}/day`,
      ].filter(Boolean).join(' · ')}
    </span>
    <span className="block font-mono text-[11px] text-neutral-500">
      {p.code}{p.pincode ? ` · ${p.pincode}` : ''}
    </span>
  </>
);
